// Re-export types from main types file
export type { 
  EntityBase,
  FileSkeleton,
  NoteSkeleton,
  SpaceSkeleton} from '../types';

import type { AppIcon } from './apps';

// Re-export Highlight types
export type { HighlightEntity, HighlightSkeleton, HighlightColor } from './entityTypes/highlight';

/**
 * Hierarchical Entity Type System
 * Uses dot notation like macOS UTIs, Java packages, MIME types
 */

// Base entity categories
export type BaseEntityCategory = 
  | 'File'
  | 'Content' 
  | 'Business'
  | 'Person'
  | 'Space'
  | 'Communication'
  | 'System'
  | 'External'
  | 'Integration';

// File hierarchy
export type FileEntityType =
  | 'File'
  | 'File.Document'
  | 'File.Document.PDF'
  | 'File.Document.Word'
  | 'File.Document.Text'
  | 'File.Document.Markdown'
  | 'File.Image'
  | 'File.Image.JPEG'
  | 'File.Image.PNG'
  | 'File.Image.GIF'
  | 'File.Video'
  | 'File.Video.MP4'
  | 'File.Audio'
  | 'File.Audio.MP3'
  | 'File.Archive'
  | 'File.Archive.ZIP'
  | 'File.Code'
  | 'File.Code.JavaScript'
  | 'File.Code.Python';

// Content hierarchy  
export type ContentEntityType =
  | 'Content'
  | 'Content.Article'
  | 'Content.Article.BlogPost'
  | 'Content.Article.News'
  | 'Content.Note'
  | 'Content.Note.Quick'
  | 'Content.Note.Meeting'
  | 'Content.WebDocument';

// Business hierarchy
export type BusinessEntityType =
  | 'Business'
  | 'Business.Corporation'
  | 'Business.Corporation.PublicCorp'
  | 'Business.Corporation.BCorp'
  | 'Business.LLC'
  | 'Business.Partnership';

// Communication hierarchy
export type CommunicationEntityType =
  | 'Communication'
  | 'Communication.Email'
  | 'Communication.Message'
  | 'Communication.Thread';

// System hierarchy
export type SystemEntityType =
  | 'System'
  | 'System.Space'
  | 'System.Group'
  | 'System.User'
  | 'System.App'
  | 'System.AppInstance'; // Persistent app instances with state

// Project hierarchy
export type ProjectEntityType =
  | 'Project'
  | 'Project.Book'
  | 'Project.BookProject';

// External hierarchy (external/cloud file references)
export type ExternalEntityType =
  | 'External'
  | 'External.GoogleDrive'
  | 'External.GoogleDrive.Document'      // Google Docs
  | 'External.GoogleDrive.Spreadsheet'   // Google Sheets
  | 'External.GoogleDrive.Presentation'  // Google Slides
  | 'External.GoogleDrive.Form'          // Google Forms
  | 'External.GoogleDrive.Drawing'       // Google Drawings
  | 'External.GoogleDrive.PDF'           // PDF in Drive
  | 'External.GoogleDrive.Image'         // Images in Drive
  | 'External.GoogleDrive.Video'         // Videos in Drive
  | 'External.GoogleDrive.Audio'         // Audio in Drive
  | 'External.LocalFile'
  | 'External.LocalFile.Directory';

// Integration hierarchy (connected services)
export type IntegrationEntityType =
  | 'Integration'
  | 'Integration.GoogleDrive'
  | 'Integration.Gmail'
  | 'Integration.Slack'
  | 'Integration.GitHub'
  | 'Integration.Notion'
  | 'Integration.AppleNote'
  | 'Integration.GoogleCalendar';

// All valid hierarchical entity types
export type HierarchicalEntityType = 
  | FileEntityType 
  | ContentEntityType 
  | BusinessEntityType
  | CommunicationEntityType
  | SystemEntityType
  | ProjectEntityType
  | ExternalEntityType
  | IntegrationEntityType;

// Legacy types for backward compatibility
export type LegacyEntityType = 
  | 'PDF'    // -> File.Document.PDF
  | 'Note'   // -> Content.Note
  | 'Doc'    // -> Content.Article
  | 'BookProject' // -> Project.BookProject
  | 'Group'  // -> System.Group
  | 'Email'  // -> Communication.Email
  | 'Message' // -> Communication.Message
  | 'Thread'  // -> Communication.Thread
  | 'AppleNote' // -> Content.Note
  | 'WebDocument' // -> Content.WebDocument
  | 'GoogleDriveFile' // Google Drive file
  | 'LocalFile' // Local file system file
  | 'Integration' // Integration entity
  | 'APIConnection' // API Connection entity
  | 'MCPConnection' // MCP server connection entity
  | 'ManagedAPI' // River-managed API entity (system-level)
  | 'Highlight' // PDF Highlight annotation
  | 'AppInstance' // Persistent app instance with state (legacy format)
  | 'Product' // E-commerce product
  | 'Order' // E-commerce order
  | 'EntityTrigger' // Event-driven automation
  | 'Notification'; // User notification

// Complete entity type union
export type EntityType = HierarchicalEntityType | LegacyEntityType;

/**
 * Entity Type Utility Functions
 */
export class EntityTypeUtils {
  /**
   * Check if an entity type is a subtype of another
   */
  static isSubtypeOf(child: string | undefined, parent: string): boolean {
    if (!child || !parent) return false;
    return child.startsWith(parent + '.') || child === parent;
  }

  /**
   * Get the immediate parent type of an entity type
   */
  static getParent(entityType: string): string | null {
    const lastDot = entityType.lastIndexOf('.');
    return lastDot > 0 ? entityType.substring(0, lastDot) : null;
  }

  /**
   * Get all ancestor types in hierarchy order (immediate parent to root)
   * e.g., "File.Document.PDF" -> ["File.Document", "File"]
   */
  static getAncestors(entityType: string): string[] {
    const parts = entityType.split('.');
    const ancestors: string[] = [];
    
    for (let i = parts.length - 1; i > 0; i--) {
      ancestors.push(parts.slice(0, i).join('.'));
    }
    
    return ancestors;
  }

  /**
   * Get the complete hierarchy including self
   * e.g., "File.Document.PDF" -> ["File.Document.PDF", "File.Document", "File"]
   */
  static getHierarchy(entityType: string): string[] {
    return [entityType, ...this.getAncestors(entityType)];
  }

  /**
   * Get the leaf type (last part)
   * e.g., "File.Document.PDF" -> "PDF"
   */
  static getLeafType(entityType: string): string {
    const parts = entityType.split('.');
    return parts[parts.length - 1];
  }

  /**
   * Get the base category (first part)
   * e.g., "File.Document.PDF" -> "File"
   */
  static getBaseCategory(entityType: string): string {
    return entityType.split('.')[0];
  }

  /**
   * Convert legacy entity types to hierarchical ones
   */
  static convertLegacyType(legacyType: LegacyEntityType): HierarchicalEntityType | string {
    const legacyMap: Partial<Record<LegacyEntityType, HierarchicalEntityType>> = {
      'PDF': 'File.Document.PDF',
      'Note': 'Content.Note',
      'Doc': 'Content.Article',
      'Group': 'System.Group',
      'Email': 'Communication.Email',
      'Message': 'Communication.Message',
      'Thread': 'Communication.Thread',
      'AppleNote': 'Content.Note',
      'WebDocument': 'Content.WebDocument',
      'GoogleDriveFile': 'External.GoogleDrive',
      'LocalFile': 'External.LocalFile',
      'Integration': 'Integration'
    };
    
    // For new types that don't have hierarchical equivalents, return as-is
    return legacyMap[legacyType] || legacyType;
  }

  /**
   * Check if a type is a legacy type
   */
  static isLegacyType(entityType: string): entityType is LegacyEntityType {
    const legacyTypes: LegacyEntityType[] = [
      'PDF', 'Note', 'Doc', 'Group', 'Email', 'Message', 'Thread', 'AppleNote', 'WebDocument',
      'GoogleDriveFile', 'LocalFile', 'Integration', 'APIConnection', 'Highlight', 'AppInstance',
      'Product', 'Order'
    ];
    return legacyTypes.includes(entityType as LegacyEntityType);
  }

  /**
   * Normalize entity type (convert legacy to hierarchical)
   */
  static normalize(entityType: EntityType): HierarchicalEntityType | string {
    if (this.isLegacyType(entityType)) {
      return this.convertLegacyType(entityType);
    }
    return entityType as HierarchicalEntityType;
  }

  /**
   * Get file extension to hierarchical type mapping
   */
  static getFileTypeFromExtension(extension: string): FileEntityType {
    const extMap: Record<string, FileEntityType> = {
      '.pdf': 'File.Document.PDF',
      '.doc': 'File.Document.Word',
      '.docx': 'File.Document.Word',
      '.txt': 'File.Document.Text',
      '.md': 'File.Document.Markdown',
      '.jpg': 'File.Image.JPEG',
      '.jpeg': 'File.Image.JPEG',
      '.png': 'File.Image.PNG',
      '.gif': 'File.Image.GIF',
      '.mp4': 'File.Video.MP4',
      '.mp3': 'File.Audio.MP3',
      '.zip': 'File.Archive.ZIP',
      '.js': 'File.Code.JavaScript',
      '.ts': 'File.Code.JavaScript',
      '.py': 'File.Code.Python'
    };
    
    return extMap[extension.toLowerCase()] || 'File';
  }

  /**
   * Map Google Drive mimeType to specific External.GoogleDrive type
   */
  static getGoogleDriveTypeFromMimeType(mimeType: string): ExternalEntityType {
    const mimeMap: Record<string, ExternalEntityType> = {
      'application/vnd.google-apps.document': 'External.GoogleDrive.Document',
      'application/vnd.google-apps.spreadsheet': 'External.GoogleDrive.Spreadsheet',
      'application/vnd.google-apps.presentation': 'External.GoogleDrive.Presentation',
      'application/vnd.google-apps.form': 'External.GoogleDrive.Form',
      'application/vnd.google-apps.drawing': 'External.GoogleDrive.Drawing',
      'application/pdf': 'External.GoogleDrive.PDF'
    };
    
    // Check exact matches first
    if (mimeMap[mimeType]) {
      return mimeMap[mimeType];
    }
    
    // Check mime type prefixes
    if (mimeType.startsWith('image/')) {
      return 'External.GoogleDrive.Image';
    }
    if (mimeType.startsWith('video/')) {
      return 'External.GoogleDrive.Video';
    }
    if (mimeType.startsWith('audio/')) {
      return 'External.GoogleDrive.Audio';
    }
    
    // Default fallback
    return 'External.GoogleDrive';
  }
}

// Skeleton types for different content formats
export interface BaseSkeleton {
  childEntities?: string[];
  '@type'?: string;
  
  // Content format tracking (for LLM entity provider selection)
  contentFormat?: 'prosemirror_json' | 'simple_nodes' | 'simple_text' | 'y.js' | 'image_url' | 'video_url' | 'audio_url' | 'form_schema' | 'markdown' | 'csv' | 'tabular' | 'code' | 'structured' | 'generic';
  
  // Document content formats
  content?: any; // ProseMirror JSON for Google Docs imports
  pdfAsDocument?: string; // Serialized simple nodes for PDFs
  wordAsDocument?: string; // Serialized simple nodes for Word docs
  
  // File metadata
  fileName?: string;
  fileSize?: number;
  mimeType?: string;
  
  // Image-specific fields
  width?: number;
  height?: number;
  format?: string;
  
  // Other common fields (extensible)
  [key: string]: any;
}

// ---- MCP Connection types ----

export interface MCPToolManifest {
  name: string;
  description: string;
  inputSchema: {
    type: 'object';
    properties: Record<string, any>;
    required?: string[];
  };
}

export interface MCPResourceManifest {
  uri: string;
  name: string;
  description?: string;
  mimeType?: string;
}

export interface MCPConnectionSkeleton extends BaseSkeleton {
  '@type': 'MCPConnection';
  serverType: 'stdio' | 'http';
  // env stored as single Fernet-encrypted JSON blob, decrypted server-side only
  stdio?: { command: string; args: string[]; env?: string; cwd?: string };
  http?: { url: string; authType: 'none' | 'bearer' | 'api_key'; headers?: string };
  manifest?: {
    tools: MCPToolManifest[];
    resources?: MCPResourceManifest[];
    lastDiscoveredAt: string;
  };
  status: 'disconnected' | 'connecting' | 'connected' | 'reconnecting' | 'error';
  connectionData?: {
    startedAt?: string;
    lastUsedAt?: string;
    usageCount?: number;
  };
  lastError?: string;
  lastErrorAt?: string;
  category?: string;
  templateId?: string;
  settings?: {
    autoReconnect?: boolean;
    trustLevel?: 'auto' | 'ask';
  };
}

// Base API response type
export interface EntityResponse {
  _id: string;
  id?: string; // Some responses include id field
  llmId?: string; // Human-readable ID for LLM interactions
  entityType: string;
  type?: string; // Type field for compatibility
  name: string;
  skeleton?: BaseSkeleton;
  created_at: string;
  updated_at: string;
  appId?: string;
  icon?: string | AppIcon; // Supports both legacy string and structured AppIcon
}

// BaseEntityType is the core interface that all entities in the system inherit from.
export interface BaseEntityType<T = BaseSkeleton> {
  _id: string;
  id: string;
  llmId?: string; // Human-readable ID for LLM interactions
  type: string;
  name: string;
  entityType: string;
  created_at: string;
  updated_at: string;
  skeleton?: T;
  isSystemGroup?: boolean;
  children?: BaseEntityType[];
  icon?: string | AppIcon; // Supports both legacy string and structured AppIcon
  trashed?: boolean; // Soft delete flag (recoverable from trash)
  hidden?: boolean; // Hidden from lists flag (system entities like avatars - not shown in UI)
  links?: {
    [relationType: string]: string[];  // Entity IDs organized by relationship type
  };
  
  // ✅ Custom Metadata System (tags/chips for organization)
  customMetadata?: Array<{
    key: string;
    value: string;
    color: string;
    type: 'tag' | 'label' | 'category' | 'custom';
    addedAt: string;
    addedBy: string;
  }>;
  metadataKeys?: string[];  // Denormalized array for fast filtering
}

// Make it a module
export {};

// EXAMPLE: Note Entity
// {
//     "_id": {
//       "$oid": "677570acc8bc2ba5fd6fda66"
//     },
//     "entityType": "Note",
//     "created_at": {
//       "$date": "2025-01-01T16:43:24.420Z"
//     },
//     "skeleton": {
//       "@type": "Note",
//       "content": "[object Object]",
//       "ydoc": [
//         1,
//         9,
//         ...
//         2
//       ]
//     },
//     "updated_at": {
//       "$date": "2025-01-01T16:43:25.826Z"
//     }
//   }

export interface EntityQueryParams {
  type?: string;  // Maps to entityType in MongoDB
  userId?: string;
  entityType?: string;  // Direct MongoDB field mapping
  name?: string;
  appId?: string;
  created_at?: string;
  updated_at?: string;
  [key: string]: any; // Allow for additional MongoDB query parameters
}
